/* // src/pages/reports/UserSubscriptionLogs.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import {
  Box,
  Button,
  Stack,
  TextField,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { get,post } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

const formatDisplayDateTime = (date) => {
  if (!date) return '';

  let parsedDate;

  // If timestamp (number)
  if (typeof date === 'number') {
    parsedDate = new Date(date);
  }
  // If string
  else if (typeof date === 'string') {
    // Fix for "yyyy-MM-dd HH:mm:ss"
    parsedDate = new Date(date.replace(' ', 'T'));
  } else {
    parsedDate = new Date(date);
  }

  return isNaN(parsedDate.getTime())
    ? ''
    : parsedDate.toLocaleDateString('en-GB') + ' ' +
      parsedDate.toLocaleTimeString('en-GB');
};

// Define columns for the report download service
const columns = [
  // { field: 'user_id', headerName: 'User_ID', width: 90 },
  { field: 'customerId', headerName: 'Customer ID', width: 120 },
  { field: 'user', headerName: 'Name', width: 200 },
  { field: 'email', headerName: 'E-mail', width: 200 },
  { field: 'phone', headerName: 'Phone', width: 200 },
  { field: 'stb_no', headerName: 'Unique STB number', width: 200 },
  { field: 'packageNames', headerName: 'Packages', width: 200 },
  { field: 'billing_address', headerName: 'Billing address', width: 200 },
  { field: 'installation_address', headerName: 'Installation address', width: 200 },
  { field: 'subscription_plan', headerName: 'Status', width: 100 },
  //{ field: 'updated_date', headerName: 'Created', width: 200 },
  
  { 
    field: 'created_date', 
    headerName: 'Created Date', 
    width: 170,
    valueGetter: (params) => {
      return formatDisplayDateTime(params.row.created_date);
    }
  },{ 
    field: 'updated_date', 
    headerName: 'Updated Date', 
    width: 170,
    valueGetter: (params) => {
      return formatDisplayDateTime(params.row.updated_date);
    }
  }
];

const statusOptions = [
  { id: 1, lable: 'All', value: 'all' },
  { id: 2, lable: 'Active', value: 'active' },
  { id: 3, lable: 'Inactive', value: 'inactive' },
];

const UserSubscriptionLogs = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [rows, setRows] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [packageChannels, setPackageChannels] = useState([]);
  const [subscriberCounts, setSubscriberCounts] = useState([]);
  const [subscriberSubscriptions, setSubscriberSubscriptions] = useState([]);
  const [subscriptionAgeing, setSubscriptionAgeing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const [statusFilter, setStatusFilter] = useState('all');
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const [auditLoading, setAuditLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleGenerateAuditReport = async () => {
  if (!startDate || !endDate) {
    showSnackbar('Please select both start and end dates', 'error');
    return;
  }

  try {
    setAuditLoading(true);

    const payload = {
      reportType: 'USER_SUBSCRIPTION_LOGS',
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
      vendorId,
      status: statusFilter === 'all' ? null : statusFilter
    };

    const response = await post(
      `${process.env.REACT_APP_BASE_API_URL}/api/audit-report/generate`,
      payload
    );

    if (response?.token) {
      showSnackbar(response.token, 'success');
    }
  } catch (error) {
    console.error(error);
    showSnackbar('Failed to generate audit report', 'error');
  } finally {
    setAuditLoading(false);
  }
};

  // Existing: Channel Status Report Fetch
  useEffect(() => {
  if (!startDate || !endDate || tabIndex !== 0) return;

  const fetchData = async () => {
    setLoading(true);
    try {
      const from = dayjs(startDate).format('YYYY-MM-DD HH:mm:ss');
      const to = dayjs(endDate).format('YYYY-MM-DD HH:mm:ss');
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getUserSubscriptionLogReport?fromDate=${from}&toDate=${to}`);
      const dataWithId = response.map((item: any, index: number) => ({
        ...item,
        id: index + 1,
        createdDateStr: dayjs(item.createdDate).format('DD/MM/YYYY HH:mm:ss'),
        // Ensure customerId is available, fallback to user_id if needed
        customerId: item.customerId || (item.user_id ? String(item.user_id) : ''),
      }));
      console.log(dataWithId);
      setRows(dataWithId);
    } catch (error) {
      console.error('Error fetching channel report data:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [startDate, endDate, tabIndex, vendorId]);

 

  // Generate timestamped filename for Excel only
  const generateTimestampedFilename = (baseName: string, start: Dayjs | null, end: Dayjs | null, status: string) => {
    const from = start ? dayjs(start).format('YYYY-MM-DD') : 'NA';
    const to = end ? dayjs(end).format('YYYY-MM-DD') : 'NA';
    const time = dayjs().format('HH-mm-ss');
    const statusSuffix = status !== 'all' ? `_${status}` : '';
    return `${baseName}${statusSuffix}_${from}_to_${to}_${time}`;
  };

  // Keep your original Excel logic with loading state
const handleDownloadExcel = () => {
  if (!startDate || !endDate) {
    alert('Please select both start and end dates');
    return;
  }

  if (tabIndex !== 0) {
    alert('Download is only available for User Subscription Logs tab');
    return;
  }

  setDownloading(prev => ({ ...prev, excel: true }));
  
  try {
    const from = dayjs(startDate).format('YYYY-MM-DD');
    const to = dayjs(endDate).format('YYYY-MM-DD');

    // Step 1: Prepare headers - REMOVED User_ID, ADDED Customer ID
    const headers = [
      'Customer ID', // Changed from 'User_ID'
      'Name',
      'E-mail',
      'Phone',
      'Unique STB number',
      'Packages',
      'Billing address',
      'Installation address',
      'Status',
      'Created Date',
      'Updated Date'
    ];

    // Step 2: Prepare data rows from filtered UI data
    const data = filteredRows.map((row: any) => [
      row.customerId || '', // Changed from row.user_id
      row.user || '',
      row.email || '',
      row.phone || '',
      row.stb_no || '',
      row.packageNames || '',
      row.billing_address || '',
      row.installation_address || '',
      row.subscription_plan || '',
      formatDisplayDateTime(row.created_date) || '',
      formatDisplayDateTime(row.updated_date) || ''
    ]);

    // Step 3: Combine headers and rows
    const finalData = [headers, ...data];

    // Step 4: Create worksheet and workbook
    const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    const workbook = XLSX.utils.book_new();

    // Step 5: Add worksheet with a safe name (max 31 chars)
    const sheetName = 'User_Subscription_Logs';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Step 6: Generate Excel buffer and save
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const filename = generateTimestampedFilename('User_Subscription_Logs', startDate, endDate, statusFilter) + '.xlsx';
    saveAs(blob, filename);
  } catch (error) {
    console.error('Download failed:', error);
  } finally {
    setDownloading(prev => ({ ...prev, excel: false }));
  }
};

  // Single download handler for PDF using common service
  const handleDownloadPDF = async () => {
  if (!startDate || !endDate) {
    alert('Please select both start and end dates');
    return;
  }

  if (tabIndex !== 0) {
    alert('Download is only available for User Subscription Logs tab');
    return;
  }

  setDownloading(prev => ({ ...prev, pdf: true }));
  
  try {
    const fromDate = startDate ? dayjs(startDate).format('DD/MM/YYYY') : 'Not Set';
    const toDate = endDate ? dayjs(endDate).format('DD/MM/YYYY') : 'Not Set';
    const fromStr = startDate ? dayjs(startDate).format('YYYY-MM-DD') : 'NA';
    const toStr = endDate ? dayjs(endDate).format('YYYY-MM-DD') : 'NA';
    const statusFilterText = statusFilter !== 'all' ? statusFilter.toUpperCase() : 'All Statuses';
    const searchFilterText = searchTerm || 'No Search Filter';
    
    // Generate custom filename
    const statusSuffix = statusFilter !== 'all' ? `_${statusFilter}` : '';
    const customFileName = `User_Subscription_Logs${statusSuffix}_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.pdf`;
    
    // Get subscription status distribution
    const statusDistribution: Record<string, number> = {};
    filteredRows.forEach((row: any) => {
      const status = row.subscription_plan || 'Unknown';
      statusDistribution[status] = (statusDistribution[status] || 0) + 1;
    });

    // Update PDF columns - remove user_id, add customerId
    const pdfColumns = [
      { field: 'customerId', headerName: 'Customer ID', width: 120 },
      { field: 'user', headerName: 'Name', width: 200 },
      { field: 'email', headerName: 'E-mail', width: 200 },
      { field: 'phone', headerName: 'Phone', width: 200 },
      { field: 'stb_no', headerName: 'Unique STB number', width: 200 },
      { field: 'packageNames', headerName: 'Packages', width: 200 },
      { field: 'billing_address', headerName: 'Billing address', width: 200 },
      { field: 'installation_address', headerName: 'Installation address', width: 200 },
      { field: 'subscription_plan', headerName: 'Status', width: 100 },
      { field: 'created_date', headerName: 'Created Date', width: 170 },
      { field: 'updated_date', headerName: 'Updated Date', width: 170 },
    ];

    const success = await reportDownloadService.downloadReport({
      title: `User Subscription Logs${statusFilter !== 'all' ? ` - ${statusFilter.toUpperCase()}` : ''}`,
      columns: pdfColumns,
      rows: filteredRows,
      month: startDate ? startDate.month() + 1 : undefined,
      year: startDate ? startDate.year() : undefined,
      fileName: customFileName,
    }, 'pdf');
    
    if (!success) {
      alert('Failed to generate PDF. Please try again.');
    }
  } catch (error) {
    console.error('Error downloading PDF:', error);
    alert('Error generating PDF. Please try again.');
  } finally {
    setDownloading(prev => ({ ...prev, pdf: false }));
  }
};

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (row.subscription_plan && 
       row.subscription_plan.toLowerCase() === statusFilter.toLowerCase());
    
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <Helmet>
        <title>User Subscription Logs</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="User Subscription Logs"
              subHeading="recording all logs including information and data concerning the subscribers"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasPermission(["OWNER"]) && tabIndex === 0 && (
              <>
  <Button
    variant="contained"
    color="primary"
    startIcon={<DownloadIcon />}
    onClick={handleDownloadExcel}
    disabled={downloading.excel}
  >
    {downloading.excel ? 'Generating...' : 'Download Excel'}
  </Button>

  <Button
    variant="outlined"
    color="secondary"
    startIcon={<PictureAsPdfIcon />}
    onClick={handleDownloadPDF}
    disabled={downloading.pdf}
  >
    {downloading.pdf ? 'Generating...' : 'Download PDF'}
  </Button>

  <Button
    size="small"
    variant="contained"
    color="success"
    onClick={handleGenerateAuditReport}
    disabled={auditLoading}
    sx={{ minWidth: 140, height: 36 }}
  >
    {auditLoading ? 'Generating...' : 'Generate Report'}
  </Button>
</>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>



      <Box sx={{ px: 2, py: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            {tabIndex === 0 && (
              <>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ minWidth: 200 }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.id} value={status.value}>
                        {status.lable}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <DatePicker
              label="Start Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      <Box sx={{ height:"400px", px: 2, py: 1, overflow: 'auto' }}>
        {tabIndex === 0 && (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            getRowHeight={() => 'auto'}
            loading={loading}
            sx={{
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell': { alignItems: 'start', py: 1.5 },
            }}
          />
        )}

        {tabIndex === 1 && (
          <pre>{JSON.stringify(monthlyData, null, 2)}</pre>
        )}

        {tabIndex === 2 && (
          <pre>{JSON.stringify(packageChannels, null, 2)}</pre>
        )}

        {tabIndex === 3 && (
          <pre>{JSON.stringify(subscriberCounts, null, 2)}</pre>
        )}

        {tabIndex === 4 && (
          <pre>{JSON.stringify(subscriberSubscriptions, null, 2)}</pre>
        )}

        {tabIndex === 5 && (
          <pre>{JSON.stringify(subscriptionAgeing, null, 2)}</pre>
        )}
      </Box>
    </>
  );
};

export default UserSubscriptionLogs; */

// src/pages/reports/KeysLogs.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { get,post } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

// Define columns for the report download service
const columns = [
  { field: 'id', headerName: 'ID', width: 90 },
  { field: 'customerId', headerName: 'Customer ID', width:150 },
  { field: 'subscriptionId', headerName: 'subscription ID', width:150 },
  { field: 'command', headerName: 'Command', flex: 1, minWidth: 300 },
  { field: 'addedPackages', headerName: 'Added Packages', flex: 1, minWidth: 150 },
  { field: 'removedPackages', headerName: 'Removed Packages', flex: 1, minWidth: 150 },
  { field: 'action', headerName: 'Action', flex: 1, minWidth: 80 },
  //{ field: 'entityName', headerName: 'Entity Name', flex: 1, minWidth: 150 },
  //{ field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
  { field: 'status', headerName: 'Status', flex: 1, minWidth: 120 },
  { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 150 },
  { 
    field: 'createdDateStr', 
    headerName: 'Created Date', 
    flex: 1, 
    minWidth: 170, // Slightly wider for DD/MM/YYYY format
    valueGetter: (params) => {
      if (!params.row.createdDate) return '';
      return dayjs(params.row.createdDate).format('DD/MM/YYYY HH:mm:ss'); // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
    }
  },
];

const UserSubscriptionLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const [auditLoading, setAuditLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const from = dayjs(startDate).format('YYYY-MM-DD');
        const to = dayjs(endDate).format('YYYY-MM-DD');

        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getSubscriptionLog?fromDate=${from}&toDate=${to}&entityName=USER SUBSCRIPTION`
        );

        const dataWithId = response?.data.map((item: any, index: number) => ({
          id: index + 1,
          createdDateStr: dayjs(item.createdDate).format('DD/MM/YYYY HH:mm:ss'), // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
          ...item,
        }));

        console.log('dataWithId', dataWithId);
        setRows(dataWithId);
      } catch (error) {
        console.error('Error fetching audit log data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, vendorId]);

  const handleGenerateAuditReport = async () => {
  if (!startDate || !endDate) {
    showSnackbar('Please select both start and end dates', 'error');
    return;
  }

  try {
    setAuditLoading(true);

    const payload = {
      reportType: 'SUBSCRIPTION_LOG',
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
      vendorId,
      entityName: 'USER SUBSCRIPTION'
    };

    const response = await post(
      `${process.env.REACT_APP_BASE_API_URL}/api/audit-report/generate`,
      payload
    );

    if (response?.token) {
      showSnackbar(response.token, 'success');
    }
  } catch (error) {
    console.error(error);
    showSnackbar('Failed to generate audit report', 'error');
  } finally {
    setAuditLoading(false);
  }
};

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return matchesSearch;
  });

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const fromStr = dayjs(startDate).format('YYYY-MM-DD');
      const toStr = dayjs(endDate).format('YYYY-MM-DD');
      
      // Generate custom filename
      const customFileName = `SUBSCRIPTION_LOG_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Get entity distribution for statistics
      const entityTypes: Record<string, number> = {};
      filteredRows.forEach(row => {
        const entity = row.entityName || 'Unknown';
        entityTypes[entity] = (entityTypes[entity] || 0) + 1;
      });

      // Get action type statistics
      const actionStats: Record<string, number> = {};
      filteredRows.forEach(row => {
        const action = row.action || 'Unknown';
        actionStats[action] = (actionStats[action] || 0) + 1;
      });

      const success = await reportDownloadService.downloadReport({
        title: 'SUBSCRIPTION LOGS Report',
        columns,
        rows: filteredRows,
        month: startDate.month() + 1,
        year: startDate.year(),
        fileName: customFileName,
        
        // Excel specific configuration
        excelHeaders: [
  'ID',
  'Customer ID',
  'subscription ID',
  'Command',
  'Added Packages',
  'Removed Packages',
  'Action',
  'Status',
  'Created By',
  'Created Date'
],
        excelRowMapper: (row: any) => [
          row.id,
  row.customerId,
  row.subscriptionId,
  row.command,
  row.addedPackages,
  row.removedPackages,
  row.action,
  row.status,
  row.createdBy,
  
          row.createdDateStr || dayjs(row.createdDate).format('DD/MM/YYYY HH:mm:ss') || '' // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title>SUBSCRIPTION LOG</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="SUBSCRIPTION LOG REPORT"
              subHeading=""
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  {hasPermission(["OWNER"]) && (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={() => handleDownload('excel')}
        disabled={downloading.excel}
      >
        {downloading.excel ? 'Generating...' : 'Download Excel'}
      </Button>

      <Button
        variant="outlined"
        color="secondary"
        startIcon={<PictureAsPdfIcon />}
        onClick={() => handleDownload('pdf')}
        disabled={downloading.pdf}
      >
        {downloading.pdf ? 'Generating...' : 'Download PDF'}
      </Button>

      {/* <Button
        size="small"
        variant="contained"
        color="success"
        onClick={handleGenerateAuditReport}
        disabled={auditLoading}
        sx={{ minWidth: 140, height: 36 }}
      >
        {auditLoading ? 'Generating...' : 'Generate Report'}
      </Button> */}
    </>
  )}
</Box>
        </Box>
      </PageTitleWrapper>

      {/* Filter Section */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />

            <DatePicker
              label="Start Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* Table Section */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              py: 1.5
            },
          }}
        />
      </Box>
    </>
  );
};

export default UserSubscriptionLogs;